import { useCallback } from 'react';
import {
    IntensityRegion,
    regionCenterMm,
} from '~/domain/intensity/intensityRegion';
import { Image3D } from '~/domain/patient/image3d';
import { AnnotationGoTo, useAnnotationGoTo } from '~/hooks/useAnnotationGoTo';
import { useImageForSeries } from '~/hooks/image/useImageForSeries';
import { intensityRegionListState } from '~/state/atoms/intensityToolAtoms';

export interface IntensityRegionController extends AnnotationGoTo {
    /**
     * The image the region was placed on, found by the region's own series
     * UID, so its figures are labelled with that image's unit and not with the
     * unit of whatever the main view shows now. Undefined once that series is
     * no longer loaded; the figures are then not shown at all, rather than
     * shown without a unit.
     */
    imageForRegion: (region: IntensityRegion) => Image3D | undefined;
}

/**
 * Go-to for regions, to their centre, which is on the region's slice, and the
 * image each region was placed on. Placing a region and reading its figures
 * is the engine tool's job, not this hook's.
 */
export function useIntensityRegionController(): IntensityRegionController {
    const list = useAnnotationGoTo(intensityRegionListState, regionCenterMm);
    const imageForSeries = useImageForSeries();

    const imageForRegion = useCallback(
        (region: IntensityRegion): Image3D | undefined =>
            imageForSeries(region.plane.seriesInstanceUid),
        [imageForSeries]
    );

    return { ...list, imageForRegion };
}
